import Layout from "../../components/layoutConfig/layout";
import { useState, useEffect, FormEvent, useRef } from "react";
import { motion } from "framer-motion";
import DataTable, { TableColumn } from "react-data-table-component";
import { toast } from "react-toastify";
import "react-phone-input-2/lib/bootstrap.css";
import PhoneInput from "react-phone-input-2";
import {
  FiBox,
  FiPlusCircle,
  FiCheckCircle,
  FiSearch,
  FiTrash2,
  FiEdit,
  FiUser,
  FiMail,
  FiPhone,
  FiEye,
  FiSend,
  FiX,
} from "react-icons/fi";
import { setupAPIclient } from "../../services/setupAPIclient";
import LocalStorage from "../../helpers/localstorage";
import GuidedTutorial from "../../components/common/GuidedTutorial";

interface User {
  id: number;
  email: string;
  name: string;
  number_phone: string;
  can_view: boolean | null;
  view_email: boolean | null;
  created_at: string;
  updated_at: string;
  can_edit: boolean | null;
  typeofcontract: string | null;
}

function UserPage() {
  const localStorage = new LocalStorage();
  const token = String(localStorage.getAuthToken());
  const ApiClient = setupAPIclient(token);

  const [data, setData] = useState<User[]>([]);
  const [pending, setPending] = useState<boolean>(false);
  const [filterText, setFilterText] = useState("");
  const [formUser, setFormUser] = useState({
    name: "",
    email: "",
    number_phone: "",
    can_view: false,
    view_email: false,
    can_edit: false,
    typeofcontract: "",
    passwordFront: "",
  });

  const [editId, setEditId] = useState<number | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const canViewRef = useRef<HTMLDivElement>(null);
  const canEditRef = useRef<HTMLDivElement>(null);
  const typeRef = useRef<HTMLSelectElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const iconColor = "#007bff";

  const handleGetUsers = async () => {
    try {
      setPending(true);
      const response = await ApiClient.post("/management/get/users");
      setData(response.data.success || []);
    } catch {
      toast.error("Erro ao carregar usuários");
    } finally {
      setPending(false);
    }
  };

  useEffect(() => {
    handleGetUsers();
  }, []);

  const checkDuplicate = (email: string, number_phone: string, idToIgnore?: number) => {
    const duplicateEmail = data.some((u) => u.email === email && u.id !== idToIgnore);
    const duplicatePhone = data.some((u) => u.number_phone === number_phone && u.id !== idToIgnore);

    if (duplicateEmail) {
      toast.warning("Este e-mail já está cadastrado!");
      return true;
    }
    if (duplicatePhone) {
      toast.warning("Este número de telefone já está cadastrado!");
      return true;
    }
    return false;
  };

  const handleCreateOrUpdate = async (e: FormEvent) => {
    e.preventDefault();
    const {
      name,
      email,
      number_phone,
      can_view,
      can_edit,
      typeofcontract,
      passwordFront,
    } = formUser;

    if (!name || !email || !number_phone) {
      toast.warning("Preencha todos os campos obrigatórios!");
      return;
    }

    if (checkDuplicate(email, number_phone, editId || undefined)) return;

    try {
      if (editId) {
        await ApiClient.post("/management/update/users", {
          id: editId,
          name,
          email,
          number_phone,
          can_view,
          can_edit,
          typeofcontract,
        });
        toast.success("Usuário atualizado com sucesso!");
      } else {
        await ApiClient.post("/management/create/users", {
          name,
          email,
          number_phone,
          can_view,
          can_edit,
          typeofcontract,
          passwordFront,
        });
        toast.success("Usuário criado com sucesso! Convite enviado automaticamente.");
      }

      setFormUser({
        name: "",
        email: "",
        number_phone: "",
        can_view: false,
        view_email: false,
        can_edit: false,
        typeofcontract: "",
        passwordFront: "",
      });

      setEditId(null);
      handleGetUsers();
      window.dispatchEvent(new Event("management:onboarding-updated"));
    } catch {
      toast.error("Erro ao salvar usuário");
    }
  };

  const handleEdit = (user: User) => {
    setEditId(user.id);
    setFormUser({
      name: user.name,
      email: user.email,
      number_phone: user.number_phone,
      can_view: Boolean(user.can_view),
      view_email: Boolean(user.view_email),
      can_edit: Boolean(user.can_edit),
      typeofcontract: user.typeofcontract ?? "",
      passwordFront: "",
    });
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setFormUser({
      name: "",
      email: "",
      number_phone: "",
      can_view: false,
      view_email: false,
      can_edit: false,
      typeofcontract: "",
      passwordFront: "",
    });
    toast.info("Edição cancelada.");
  };

  const handleSendInvite = async (email: string) => {
    try {
      await ApiClient.post("/management/invite/users", { email });
      toast.success(`Convite enviado para ${email}.`);
     // handleGetUsers();
    } catch {
      toast.error("Erro ao enviar convite.");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Deseja realmente excluir este usuário?")) return;

    try {
      await ApiClient.post("/management/delete/users", { id });
      toast.success("Usuário excluído com sucesso!");
      handleGetUsers();
    } catch {
      toast.error("Erro ao excluir usuário");
    }
  };

  const filteredItems = data.filter((item) =>
    item.name.toLowerCase().includes(filterText.toLowerCase())
  );

  const columns: TableColumn<User>[] = [
    { name: "ID", selector: (row) => row.id, sortable: true, width: "70px" },

    {
      name: "Nome",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FiUser color={iconColor} /> {row.name}
        </div>
      ),
    },

    {
      name: "E-mail",
      selector: (row) => row.email,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FiMail color={iconColor} /> {row.email}
        </div>
      ),
    },

    {
      name: "Telefone",
      selector: (row) => row.number_phone,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FiPhone color={iconColor} /> +{row.number_phone}
        </div>
      ),
    },

    {
      name: "Pode Ver Contratos?",
      selector: (row) => (row.can_view ? "Sim" : "Não"),
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FiEye color={row.can_view ? "#28a745" : "#dc3545"} />
          {row.can_view ? "Sim" : "Não"}
        </div>
      ),
    },

    {
      name: "Status do Convite",
      selector: (row) => (row.view_email ? "Aceitou" : "Pendente"),
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FiMail color={row.view_email ? "#28a745" : "#ffc107"} />
          <span style={{ color: row.view_email ? "#28a745" : "#ffc107" }}>
            {row.view_email ? "✅ Aceitou" : "⏳ Pendente"}
          </span>
        </div>
      ),
    },

    {
      name: "Ações",
      cell: (row) => (
        <div className="d-flex gap-2">
          <motion.button
            whileHover={{ scale: 1.1 }}
            className="btn btn-sm btn-outline-primary"
            style={{background:"#5dade2", color:"white"}}
            onClick={() => handleEdit(row)}
          >
            <FiEdit />
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.1 }}
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDelete(row.id)}
          >
            <FiTrash2 />
          </motion.button>
        </div>
      ),
    },
  ];

  return (
    <Layout>
      <GuidedTutorial
        storageKey="management_guided_user_v1"
        enabled={localStorage.getStep() === "1"}
        nextSystemStep="2"
        steps={[
          { targetRef: nameRef as any, title: "", description: "Digite o nome do signatário para iniciar o cadastro." },
          { targetRef: emailRef as any, title: "", description: "Informe o e-mail para convite e comunicação." },
          { targetRef: phoneInputRef as any, title: "", description: "Preencha o telefone do signatário.", elevateParentSelectors: [] },
          { targetRef: canViewRef as any, title: "", description: "Defina as permissões de visualização." },
          { targetRef: canEditRef as any, title: "", description: "Defina a permissão de edição de contratos." },
          { targetRef: typeRef as any, title: "", description: "Selecione o tipo de contratação do signatário." },
          { targetRef: submitButtonRef as any, title: "", description: "Clique em cadastrar usuário para finalizar este passo do tutorial guiado." },
        ]}
      />

      <div className="mb-4">
        <h4 className="d-flex align-items-center gap-2 mt-3">
          <FiBox color={iconColor} size={26} /> Gerenciar Signatários
        </h4>
      </div>

      <motion.div
        className="card p-4 mb-4 shadow-sm"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h5 className="mb-3 d-flex align-items-center gap-2">
          <FiPlusCircle color={iconColor} /> {editId ? "Editar Usuário" : "Novo Signatários"}
        </h5>

        {!editId && (
          <div className="alert alert-info py-2">
            <small>Ao cadastrar, será enviado um convite automaticamente.</small>
          </div>
        )}

        <form onSubmit={handleCreateOrUpdate}>
          <div className="row g-3">

            {/* Nome */}
            <div className="col-12 col-md-6 col-lg-4">
              <label className="form-label fw-semibold">Nome</label>
              <input
                ref={nameRef}
                type="text"
                className="form-control"
                placeholder="Digite o nome"
                value={formUser.name}
                onChange={(e) =>
                  setFormUser({ ...formUser, name: e.target.value })
                }
              />
            </div>

            {/* Email */}
            <div className="col-12 col-md-6 col-lg-4">
              <label className="form-label fw-semibold">E-mail</label>
              <input
                ref={emailRef}
                type="email"
                className="form-control"
                placeholder="Digite o e-mail"
                value={formUser.email}
                onChange={(e) =>
                  setFormUser({ ...formUser, email: e.target.value })
                }
              />
            </div>

            {/* Telefone */}
            <div className="col-12 col-md-6 col-lg-4">
              <label className="form-label fw-semibold">Telefone</label>
              <PhoneInput
                country={"br"}
                value={formUser.number_phone}
                onChange={(phone) =>
                  setFormUser({ ...formUser, number_phone: phone })
                }
                inputClass="w-100"
                containerStyle={{ width: "100%" }}
                inputProps={{ ref: phoneInputRef }}
              />
            </div>

            {/* Pode ver contratos */}
            <div className="col-12 col-sm-6 col-md-4">
              <label className="form-label fw-semibold">Pode ver contratos?</label>
              <div
                ref={canViewRef}
                className="form-check form-switch mt-1 user-switch-row"
              >
                <input
                  id="user-can-view-switch"
                  className="form-check-input user-switch-input"
                  type="checkbox"
                  checked={formUser.can_view}
                  onChange={(e) =>
                    setFormUser({ ...formUser, can_view: e.target.checked })
                  }
                />
                <label className="form-check-label" htmlFor="user-can-view-switch">
                  {formUser.can_view ? "Sim" : "Não"}
                </label>
              </div>
            </div>

            {/* Pode editar contratos */}
            <div className="col-12 col-sm-6 col-md-4">
              <label className="form-label fw-semibold">Pode editar contratos?</label>
              <div
                ref={canEditRef}
                className="form-check form-switch mt-1 user-switch-row"
              >
                <input
                  id="user-can-edit-switch"
                  className="form-check-input user-switch-input"
                  type="checkbox"
                  checked={formUser.can_edit}
                  onChange={(e) =>
                    setFormUser({ ...formUser, can_edit: e.target.checked })
                  }
                />
                <label className="form-check-label" htmlFor="user-can-edit-switch">
                  {formUser.can_edit ? "Editor" : "Não Editor"}
                </label>
              </div>
            </div>

            {/* Tipo de contrato */}
            <div className="col-12 col-md-4">
              <label className="form-label fw-semibold">Tipo de Contratação</label>
              <select
                ref={typeRef}
                className="form-select"
                value={formUser.typeofcontract}
                onChange={(e) =>
                  setFormUser({ ...formUser, typeofcontract: e.target.value })
                }
              >
                <option value="">Selecione...</option>
                <option value="Contratado">Contratado</option>
                <option value="Contratante">Contratante</option>
              </select>
            </div>

            {/* Botões */}
            <div className="col-12 mt-3 d-flex flex-wrap gap-2">

              <motion.button
                ref={submitButtonRef}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                                      style={{background:"#5dade2", color:"white"}}

                type="submit"
                className="btn btn-primary d-flex align-items-center gap-2"
              >
                <FiCheckCircle /> {editId ? "Salvar Alterações" : "Cadastrar Usuário"}
              </motion.button>

              {editId && (
                <>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={handleCancelEdit}
                  >
                    <FiX /> Cancelar
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    className="btn btn-outline-success d-flex align-items-center gap-2"
                    onClick={() => handleSendInvite(formUser.email)}
                  >
                    <FiSend /> Enviar Convite
                  </motion.button>
                </>
              )}
            </div>

          </div>
        </form>

      </motion.div>

      <div className="mb-3 mt-3">
        <div className="input-group" style={{ maxWidth: "300px" }}>
          <span className="input-group-text bg-white">
            <FiSearch color={iconColor} />
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Pesquisar usuário..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>
      </div>

      <DataTable
        columns={columns}                     // colunas da tabela
        data={filteredItems}                  // dados filtrados
        progressPending={pending}             // loading enquanto carrega
        pagination                            // paginação ativa
        highlightOnHover                      // destaca linha ao passar o mouse
        pointerOnHover                        // cursor de ponteiro ao passar o mouse
        responsive                            // tabela responsiva
        striped                               // linhas listradas
        dense                                 // modo compacto
        noHeader                              // sem cabeçalho padrão

        noDataComponent="Nenhum registro encontrado"  // caso não tenha dados
      />

      <style jsx>{`
        .user-switch-row {
          display: inline-flex;
          align-items: center;
          gap: 0.55rem;
          padding: 0.35rem 0.6rem;
          border-radius: 999px;
          position: relative;
        }
        .user-switch-row .form-check-label {
          margin-bottom: 0;
          font-weight: 600;
          color: #475569;
        }
        .user-switch-input {
          cursor: pointer;
          margin-top: 0;
          flex-shrink: 0;
        }
        .user-switch-row.guided-highlight-target {
          background: #ffffff !important;
          border: 1px solid rgba(93, 173, 226, 0.38);
          opacity: 1 !important;
          filter: none !important;
          isolation: isolate;
          box-shadow:
            0 0 0 3px rgba(93, 173, 226, 0.95),
            0 0 0 7px rgba(93, 173, 226, 0.22),
            0 16px 36px rgba(15, 23, 42, 0.22) !important;
        }
        .user-switch-row.guided-highlight-target * {
          opacity: 1 !important;
          filter: none !important;
        }
        .user-switch-row.guided-highlight-target .form-check-label {
          color: #0f172a !important;
        }
        .user-switch-row.guided-highlight-target .user-switch-input {
          background-color: #ffffff !important;
          border-color: #bfdbfe !important;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%235dade2'/%3e%3c/svg%3e") !important;
          box-shadow:
            inset 0 0 0 1px rgba(255, 255, 255, 0.98),
            0 0 0 1px rgba(191, 219, 254, 0.9) !important;
        }
        .user-switch-row.guided-highlight-target .user-switch-input:checked {
          background-color: #5dade2 !important;
          border-color: #5dade2 !important;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23ffffff'/%3e%3c/svg%3e") !important;
        }
        .user-switch-row.guided-highlight-target .user-switch-input:focus {
          box-shadow:
            inset 0 0 0 1px rgba(255, 255, 255, 0.7),
            0 0 0 0.2rem rgba(93, 173, 226, 0.18) !important;
        }
        [data-theme="dark"] .user-switch-row .form-check-label {
          color: #cbd5e1;
        }
        [data-theme="dark"] .user-switch-row.guided-highlight-target {
          background: #ffffff !important;
          border-color: rgba(93, 173, 226, 0.38);
        }
        [data-theme="dark"] .user-switch-row.guided-highlight-target .form-check-label {
          color: #0f172a !important;
        }
        [data-theme="dark"] .user-switch-row.guided-highlight-target .user-switch-input {
          background-color: #ffffff !important;
          border-color: #e5e7eb !important;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%235dade2'/%3e%3c/svg%3e") !important;
        }
        [data-theme="dark"] .user-switch-row.guided-highlight-target .user-switch-input:checked {
          background-color: #5dade2 !important;
          border-color: #5dade2 !important;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23ffffff'/%3e%3c/svg%3e") !important;
        }
      `}</style>

    </Layout>
  );
}

export default UserPage;
